import { useRefresh } from 'react-admin';
import TextsmsIcon from '@mui/icons-material/Textsms';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  useCallback,
  useMemo,
  useState,
  type MouseEvent,
} from 'react';
import { adminRpcClient } from '@/providers/backendClient';
import { hasAnyEligibleBulkTranscriptionRecording } from './adminBulkTranscriptionEligibilityDisplay';

type TranscribeAllPendingButtonProps = {
  sessionNote: { id: string; mediaType?: string };
  recordings: Array<{
    status?: string;
    mediaType?: string | null;
    uploadedAudioContent?: unknown;
    uploadedAudioContentSlices?: unknown;
    audioBlobContainerName?: string | null;
    audioBlobPaths?: unknown;
  }>;
};

export const TranscribeAllPendingButton = ({
  sessionNote,
  recordings,
}: TranscribeAllPendingButtonProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const transcribeAvailable = useMemo(
    () =>
      sessionNote?.mediaType === 'audio' &&
      hasAnyEligibleBulkTranscriptionRecording(recordings),
    [recordings, sessionNote?.mediaType],
  );

  const refresh = useRefresh();

  const handleOpenDialog = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    setConfirmDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const onTranscribeAllPending = useCallback(async () => {
    setIsUpdating(true);
    setConfirmDialogOpen(false);

    const result = (await adminRpcClient(
      'PUBLIC_ADMIN_therapistRecordings_transcribeAllPending',
      [sessionNote.id, 'therapistSessionNotes'],
    ).catch((err: Error) => ({
      _rpcClientError: err.message,
    }))) as {
      success?: boolean;
      message?: string;
      error?: string | { message?: string };
      _rpcClientError?: string;
    };

    setIsUpdating(false);

    refresh();

    const failureText =
      result._rpcClientError ??
      (result.success === false
        ? String(result.message ?? 'Bulk transcription request failed')
        : undefined) ??
      (typeof result.error === 'string'
        ? result.error
        : result.error?.message);

    if (failureText) {
      alert('Error starting bulk transcription: ' + failureText);
    }
  }, [refresh, sessionNote.id]);

  return transcribeAvailable ? (
    <>
      <Button
        disabled={isUpdating}
        onClick={handleOpenDialog}
        startIcon={
          !isUpdating ? (
            <TextsmsIcon />
          ) : (
            <CircularProgress size="1rem" />
          )
        }
        aria-label="Transcribe all pending recordings for this session note"
      >
        {!isUpdating ? 'Transcribe All Pending' : 'Starting…'}
      </Button>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="transcribe-all-pending-dialog-title"
        aria-describedby="transcribe-all-pending-dialog-description"
      >
        <DialogTitle id="transcribe-all-pending-dialog-title">
          Confirm Transcribe All Pending
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="transcribe-all-pending-dialog-description">
            Start transcription for every eligible recording on this session
            note (uploaded, transcribing, failed, or re-transcribe from blob
            where applicable). Work runs asynchronously—refresh and check
            Temporal when it finishes. Use Force Aggregation afterward to
            rebuild the session note transcript.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={onTranscribeAllPending}
            color="primary"
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : null;
};
